import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/** How long the saved row stays marked before it settles back to normal. */
const FLASH_MS = 6000;

/**
 * Point the user at the row they just saved.
 *
 * Reported by accounting as "when she saves a draft it jumps to somewhere in
 * the table". The save worked and the toast fired -- it names the invoice
 * number in the corner of the screen while she is looking at a table of forty
 * rows. The invoicing lists are ordered `['-invoice_date', '-created_at']`, NOT
 * by when a row was last touched, so a draft lands wherever its invoice date
 * puts it. Prod census 2026-09-02 found live drafts at list positions
 * 2, 3, 5, 6, 8, 10, 11, 16 and 18 -- position 18 is below the fold, which is
 * why this scrolls rather than only tinting.
 *
 * The creator screens knew the id they had just written and threw it away on a
 * bare `history.push('/invoicing/outgoing')`. They now hand it over as route
 * state and this reads it back.
 *
 * Deliberately one-shot: the state is consumed on arrival, so a refresh, a
 * back-navigation or a re-render never re-flashes a stale row.
 *
 * @param {Object} deps      { sort, isSearchMode, activeFilters } -- changing
 *                           any of these clears the mark, because the row the
 *                           user is now looking at is not the one we pointed at
 * @returns {{ savedId: (number|string|null), rowRef: function }}
 */
const useSavedRowFlash = ({ sort, isSearchMode, activeFilters } = {}) => {
  const location = useLocation();
  const history = useHistory();

  // Consume the route state on the first render that carries it. Held in a
  // ref as well as state so the effect below cannot re-trigger on a re-render
  // that happens to produce the same id.
  const [savedId, setSavedId] = useState(null);
  const hasScrolled = useRef(false);

  const incomingId = location.state?.savedRowId ?? null;

  useEffect(() => {
    if (incomingId === null || incomingId === undefined) return;

    setSavedId(incomingId);
    hasScrolled.current = false;

    // Strip it immediately. A user who refreshes, or comes back later via the
    // browser's back button, must not see a row flagged as "just saved".
    history.replace({ pathname: location.pathname, search: location.search, state: null });
    // location.pathname/search are stable for the life of this list; keying on
    // the id alone is what makes this fire once per save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingId]);

  // Sorting, searching or filtering re-orders or replaces the visible rows.
  // Whatever the user is looking at now, it is not the arrangement we pointed
  // into -- so drop the mark rather than risk highlighting a different row.
  //
  // activeFilters is an OBJECT. Depending on it by identity would re-run this
  // on every render the caller happens to rebuild it on, clearing the flash
  // before the user ever sees it (the unmemoized-object-in-a-dep-array shape
  // that has bitten us before). Compare the contents, not the reference.
  const arrangementKey = JSON.stringify([
    sort?.key ?? null, sort?.direction ?? null, !!isSearchMode, activeFilters ?? null,
  ]);

  useEffect(() => {
    if (savedId === null) return;
    setSavedId(null);
    // Only the user's own re-arrangement should clear it, so this must not
    // depend on savedId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrangementKey]);

  // Fade the mark out on its own. A permanent tint would read as a status.
  useEffect(() => {
    if (savedId === null) return undefined;
    const timer = setTimeout(() => setSavedId(null), FLASH_MS);
    return () => clearTimeout(timer);
  }, [savedId]);

  /**
   * Ref callback for the saved row. Attach to EVERY row; it returns undefined
   * for the others, so only one row in the table ever carries a live ref.
   *
   * The scroll happens HERE rather than in an effect. The list fetches
   * asynchronously, so the row mounts several commits after the id arrives --
   * and React attaches this ref AFTER the passive effects of that commit, so
   * an effect reading the captured node only ever saw null. The ref callback
   * is the one place the node is guaranteed to exist.
   *
   * If the saved row is not in the fetched page -- filtered out, or beyond it
   * -- this never fires, nothing scrolls, and the toast stands on its own.
   * That is the correct degrade, not a bug.
   */
  const rowRef = useCallback(
    (id) => {
      if (savedId === null || String(id) !== String(savedId)) return undefined;
      return (node) => {
        if (!node || hasScrolled.current) return;
        hasScrolled.current = true;
        // jsdom and older browsers do not implement it; a missing scroll must
        // not take the whole list down.
        node.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      };
    },
    [savedId],
  );

  return { savedId, rowRef };
};

export default useSavedRowFlash;
